import { getValidKeycloakAccessTokenOrFallback } from '../keycloak';
import { AttachmentAsset } from '../models/standard/AttachmentAsset';
import PlanModel from '../models/standard/PlanModel';
import { ATTACHMENT_URL } from '../utils/constants';

export type AttachmentFetchType = 'preview_picture' | 'panorama' | 'pdf';

export interface AttachmentAssetResponse {
  data: AttachmentAsset[];
  totalCount: number;
}
const DEFAULT_LIMIT = 25;

export default class AttachmentApi {
  private static buildUrl(type: AttachmentFetchType, plan: PlanModel, offset = 0, limit = DEFAULT_LIMIT): string {
    const params = new URLSearchParams({
      type,
      limit: String(limit),
      offset: String(offset),
      ascending: 'false',
    });

    const queryString = params.toString();
    if (plan.kojeId !== -1 && plan.eventId !== -1) {
      // Note: per instructions, projectID is mapped from kojeId here
      return `${ATTACHMENT_URL}/project/${plan.kojeId}/event/${plan.eventId}?${queryString}`;
    }

    if (plan.kojeId !== -1 && plan.kojeLevel !== -1) {
      return `${ATTACHMENT_URL}/koje/${plan.kojeId}/kojelevel/${plan.kojeLevel}?${queryString}`;
    }

    throw new Error('PlanModel is missing required IDs (KojeID/EventID or KojeID/KojeLevel) to fetch attachments.');
  }

  static async getAttachmentsPage(type: AttachmentFetchType, token: string, plan: PlanModel, offset = 0, limit = DEFAULT_LIMIT): Promise<AttachmentAssetResponse> {
    const url = this.buildUrl(type, plan, offset, limit);
    const activeToken = await getValidKeycloakAccessTokenOrFallback(token);

    if (!activeToken) {
      throw new Error('No valid authentication token available.');
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${activeToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    const json = (await response.json()) as unknown;

    // Expected: { data: [...], totalCount: number }
    if (json && typeof json === 'object' && Array.isArray((json as any).data)) {
      const data = (json as any).data as AttachmentAsset[];
      const totalCount = typeof (json as any).totalCount === 'number' ? (json as any).totalCount : data.length;

      return { data, totalCount };
    }

    // Fallback: plain array
    if (Array.isArray(json)) {
      const data = json as AttachmentAsset[];
      return { data, totalCount: data.length };
    }

    throw new Error('Unexpected attachment API response shape');
  }
}
